// CSV task import (06 §Boards and tasks "Import: ClickUp, CSV"; M2-08). New code: the column
// mapping, cell parsing and the row → task conversion of `POST …/boards/{id}/tasks/import`. Pure;
// the API resolves seats and writes (services/api/src/imports/tasks.ts), the web wizard suggests
// a mapping from the file's header with the same rules.
//
// A mapping names, per task attribute (and per board field), the CSV column that feeds it. Column
// names are compared normalised, as the CSV reader normalises headers (trimmed, lower case, runs of
// spaces → `_`). An empty cell leaves the attribute at its default. Accepted cell formats:
//   status      a status key or name of the board, any case
//   priority    0–4, or none / urgent / high / normal / low (ClickUp's names), any case
//   dates       YYYY-MM-DD, an ISO-8601 timestamp with zone, or Unix milliseconds (ClickUp's
//               API and exports); a bare date is 00:00 UTC
//   estimate    minutes ("90"), or hours and minutes ("1h 30m", "1.5h", "45m")
//   assignees   seat ids or seat titles, separated by `;`, `|` or `,` (resolved by the API)
//   fields      by type: text/url as is; number; currency as an amount in the field's currency;
//               select / multi_select by choice key or name; date as above; checkbox
//               true/false/yes/no/1/0/x; seat as a seat id. Formula, rollup and relation fields
//               cannot be imported.
import { type FieldDefLike, normaliseInstant, READ_ONLY_TYPES } from "../boards/fields";

export const TASK_IMPORT_TARGETS = [
  "title",
  "description",
  "status",
  "priority",
  "due_at",
  "start_at",
  "estimate_minutes",
  "assignees",
  "external_id",
] as const;
export type TaskImportTarget = (typeof TASK_IMPORT_TARGETS)[number];

export const TASK_TARGET_LABELS: Record<TaskImportTarget, string> = {
  title: "Title",
  description: "Description",
  status: "Status",
  priority: "Priority",
  due_at: "Due date",
  start_at: "Start date",
  estimate_minutes: "Estimate",
  assignees: "Assignees",
  external_id: "External id",
};

/** Header names (normalised) suggested per target; ClickUp's CSV export names included. */
const SYNONYMS: Record<TaskImportTarget, readonly string[]> = {
  title: ["title", "task_name", "name", "task", "summary", "subject"],
  description: ["description", "task_content", "content", "body", "notes", "details"],
  status: ["status", "state", "stage"],
  priority: ["priority"],
  due_at: ["due_at", "due_date", "due", "deadline"],
  start_at: ["start_at", "start_date", "start"],
  estimate_minutes: ["estimate_minutes", "estimate", "time_estimate", "time_estimated"],
  assignees: ["assignees", "assignee", "assigned_to", "owner", "seat", "seats"],
  external_id: ["external_id", "task_id", "id", "key", "issue_key"],
};

/** Maximum task-attribute and field columns one mapping may name (the CSV reader allows 32). */
export const MAX_MAPPED_FIELDS = 32;

export type TaskImportMapping = {
  /** Target → CSV column (header name). `title` is required. */
  columns: Partial<Record<TaskImportTarget, string>>;
  /** Board field id → CSV column. */
  fields?: Record<string, string> | undefined;
};

export const normalizeHeader = (h: string): string => h.trim().toLowerCase().replace(/\s+/g, "_");

/** Fields a mapping may feed: every type except computed ones and relations. */
export const importableField = (f: Pick<FieldDefLike, "type">): boolean =>
  !READ_ONLY_TYPES.has(f.type) && f.type !== "relation";

/**
 * A first mapping for `header`: each target takes the first unused column whose normalised name
 * is one of its synonyms; a board field takes the column named like it. The user adjusts it.
 */
export function suggestTaskMapping(
  header: readonly string[],
  fields: readonly Pick<FieldDefLike, "id" | "name" | "type">[] = [],
): TaskImportMapping {
  const cols = header.map(normalizeHeader);
  const used = new Set<string>();
  const columns: TaskImportMapping["columns"] = {};
  for (const t of TASK_IMPORT_TARGETS) {
    const hit = SYNONYMS[t].find((s) => cols.includes(s) && !used.has(s));
    if (hit) {
      columns[t] = hit;
      used.add(hit);
    }
  }
  const out: Record<string, string> = {};
  for (const f of fields) {
    if (!importableField(f)) continue;
    const name = normalizeHeader(f.name);
    if (cols.includes(name) && !used.has(name)) {
      out[f.id] = name;
      used.add(name);
    }
  }
  return Object.keys(out).length > 0 ? { columns, fields: out } : { columns };
}

export type MappingProblem = { path: string; code: string; message: string };

/** Why `mapping` cannot be used with this file and board (empty when it can). */
export function mappingProblems(
  mapping: TaskImportMapping,
  header: readonly string[],
  fields: readonly Pick<FieldDefLike, "id" | "type">[],
): MappingProblem[] {
  const cols = new Set(header.map(normalizeHeader));
  const p: MappingProblem[] = [];
  const seen = new Map<string, string>();
  const check = (path: string, column: string) => {
    const c = normalizeHeader(column);
    if (!cols.has(c))
      p.push({ path, code: "unknown_column", message: `the file has no column ${column}` });
    const other = seen.get(c);
    if (other)
      p.push({
        path,
        code: "duplicate_column",
        message: `column ${column} already feeds ${other}`,
      });
    seen.set(c, path);
  };
  if (!mapping.columns.title)
    p.push({ path: "mapping.columns.title", code: "required", message: "map a column to title" });
  for (const t of TASK_IMPORT_TARGETS) {
    const col = mapping.columns[t];
    if (col !== undefined) check(`mapping.columns.${t}`, col);
  }
  const entries = Object.entries(mapping.fields ?? {});
  if (entries.length > MAX_MAPPED_FIELDS)
    p.push({ path: "mapping.fields", code: "too_many", message: "too many mapped fields" });
  for (const [id, col] of entries) {
    const f = fields.find((x) => x.id === id);
    const path = `mapping.fields.${id}`;
    if (!f) p.push({ path, code: "invalid_reference", message: "not a field of this board" });
    else if (!importableField(f))
      p.push({ path, code: "invalid_value", message: `a ${f.type} field cannot be imported` });
    check(path, col);
  }
  return p;
}

// Cells ------------------------------------------------------------------------------------------

const PRIORITY_NAMES: Record<string, number> = { none: 0, urgent: 1, high: 2, normal: 3, low: 4 };

/** 0–4 or a priority name; null when unreadable. */
export function parsePriority(s: string): number | null {
  const v = s.trim().toLowerCase();
  if (/^[0-4]$/.test(v)) return Number(v);
  return PRIORITY_NAMES[v] ?? null;
}

/** An ISO date or timestamp, or Unix milliseconds, as an ISO UTC timestamp; null when unreadable. */
export function parseWhen(s: string): string | null {
  const v = s.trim();
  if (/^\d{12,14}$/.test(v)) {
    const d = new Date(Number(v));
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  }
  return normaliseInstant(v);
}

/** Minutes from "90", "1h 30m", "1.5h", "45m" or "2 h"; null when unreadable. */
export function parseEstimate(s: string): number | null {
  const v = s.trim().toLowerCase();
  if (/^\d+$/.test(v)) return Number(v);
  const m = /^(?:(\d+(?:\.\d+)?)\s*h(?:ours?|rs?)?)?\s*(?:(\d+)\s*m(?:in(?:utes?)?)?)?$/.exec(v);
  if (!m || (m[1] === undefined && m[2] === undefined)) return null;
  return Math.round(Number(m[1] ?? 0) * 60 + Number(m[2] ?? 0));
}

/** A list cell split on `;`, `|` or `,`, trimmed, empty parts dropped. */
export function splitList(s: string): string[] {
  return s
    .split(/[;|,]/)
    .map((x) => x.trim())
    .filter((x) => x !== "");
}

const TRUE = new Set(["true", "yes", "y", "1", "x", "✓", "checked"]);
const FALSE = new Set(["false", "no", "n", "0", "", "unchecked"]);

/** The field value a cell stands for, or an error message. */
export function fieldCell(
  def: FieldDefLike,
  cell: string,
): { ok: true; value: unknown } | { ok: false; message: string } {
  const v = cell.trim();
  const choice = (x: string) => {
    const l = x.toLowerCase();
    return (def.options.choices ?? []).find((c) => c.key === x || c.name.toLowerCase() === l);
  };
  switch (def.type) {
    case "text":
    case "url":
      return { ok: true, value: v };
    case "number": {
      const n = Number(v);
      return v !== "" && Number.isFinite(n)
        ? { ok: true, value: n }
        : { ok: false, message: "a number" };
    }
    case "currency": {
      const n = Number(v);
      if (v === "" || !Number.isFinite(n)) return { ok: false, message: "an amount" };
      const value: Record<string, unknown> = { amount_micros: Math.round(n * 1_000_000) };
      if (def.options.currency) value.currency = def.options.currency;
      return { ok: true, value };
    }
    case "select": {
      const c = choice(v);
      return c ? { ok: true, value: c.key } : { ok: false, message: `no choice "${v}"` };
    }
    case "multi_select": {
      const parts = splitList(v);
      const missing = parts.filter((x) => !choice(x));
      if (missing.length > 0)
        return { ok: false, message: `no choice ${missing.map((m) => `"${m}"`).join(", ")}` };
      return { ok: true, value: parts.map((x) => choice(x)?.key as string) };
    }
    case "date": {
      const at = parseWhen(v);
      return at ? { ok: true, value: at } : { ok: false, message: "a date (YYYY-MM-DD)" };
    }
    case "checkbox": {
      const l = v.toLowerCase();
      if (TRUE.has(l)) return { ok: true, value: true };
      if (FALSE.has(l)) return { ok: true, value: false };
      return { ok: false, message: "true or false" };
    }
    case "seat":
      return { ok: true, value: v.toLowerCase() };
    default:
      return { ok: false, message: `a ${def.type} field cannot be imported` };
  }
}

// Rows -------------------------------------------------------------------------------------------

export type BoardShape = {
  statuses: readonly { key: string; name: string }[];
  fields: readonly FieldDefLike[];
};

export type TaskRowInput = {
  title: string;
  body_markdown?: string;
  status?: string;
  priority?: number;
  due_at?: string;
  start_at?: string;
  estimate_minutes?: number;
  fields?: Record<string, unknown>;
};

export type RowError = { column: string; code: string; message: string };

export type ParsedTaskRow =
  | { ok: true; task: TaskRowInput; assignees: string[]; externalId: string | null }
  | { ok: false; errors: RowError[] };

export const TITLE_MAX = 500;

/** One CSV row (cells by normalised header) → a task input, or every problem of the row. */
export function parseTaskRow(
  cells: Readonly<Record<string, string>>,
  mapping: TaskImportMapping,
  board: BoardShape,
): ParsedTaskRow {
  const errors: RowError[] = [];
  const col = (t: TaskImportTarget) => {
    const c = mapping.columns[t];
    return c === undefined ? undefined : normalizeHeader(c);
  };
  const cell = (t: TaskImportTarget) => {
    const c = col(t);
    const v = c === undefined ? "" : (cells[c] ?? "").trim();
    return { c: c ?? t, v };
  };
  const fail = (column: string, code: string, message: string) =>
    errors.push({ column, code, message });

  const title = cell("title");
  if (title.v === "") fail(title.c, "required", "a title is required");
  else if (title.v.length > TITLE_MAX) fail(title.c, "too_long", `at most ${TITLE_MAX} characters`);
  const task: TaskRowInput = { title: title.v };

  const description = cell("description");
  if (description.v !== "") task.body_markdown = description.v;

  const status = cell("status");
  if (status.v !== "") {
    const l = status.v.toLowerCase();
    const s = board.statuses.find((x) => x.key === l || x.name.toLowerCase() === l);
    if (s) task.status = s.key;
    else fail(status.c, "status_invalid", `no status "${status.v}" on this board`);
  }

  const priority = cell("priority");
  if (priority.v !== "") {
    const p = parsePriority(priority.v);
    if (p === null) fail(priority.c, "invalid_value", "0–4 or none, urgent, high, normal, low");
    else task.priority = p;
  }

  for (const t of ["due_at", "start_at"] as const) {
    const d = cell(t);
    if (d.v === "") continue;
    const at = parseWhen(d.v);
    if (at === null) fail(d.c, "invalid_value", "a date (YYYY-MM-DD) or ISO timestamp");
    else task[t] = at;
  }
  if (task.due_at && task.start_at && task.due_at < task.start_at)
    fail(cell("due_at").c, "invalid_value", "the due date is before the start date");

  const estimate = cell("estimate_minutes");
  if (estimate.v !== "") {
    const m = parseEstimate(estimate.v);
    if (m === null) fail(estimate.c, "invalid_value", 'minutes, or hours and minutes ("1h 30m")');
    else task.estimate_minutes = m;
  }

  const assignees = splitList(cell("assignees").v);
  if (assignees.length > 20) fail(cell("assignees").c, "too_many", "at most 20 assignees");

  const values: Record<string, unknown> = {};
  for (const [id, column] of Object.entries(mapping.fields ?? {})) {
    const def = board.fields.find((f) => f.id === id);
    const c = normalizeHeader(column);
    const v = (cells[c] ?? "").trim();
    if (!def || v === "") continue;
    const r = fieldCell(def, v);
    if (r.ok) values[id] = r.value;
    else fail(c, "invalid_value", `${def.name}: ${r.message}`);
  }
  if (Object.keys(values).length > 0) task.fields = values;

  const ext = cell("external_id").v;
  if (errors.length > 0) return { ok: false, errors };
  return { ok: true, task, assignees, externalId: ext === "" ? null : ext.slice(0, 200) };
}

/**
 * The identity of a row for re-import: its external id when mapped and present, else every
 * mapped cell in a fixed order. The API hashes it into the task's `client_id`, so committing
 * the same file twice creates nothing the second time (and a repeated row is a duplicate).
 */
export function rowIdentity(
  cells: Readonly<Record<string, string>>,
  mapping: TaskImportMapping,
): string {
  const ext = mapping.columns.external_id;
  const id = ext === undefined ? "" : (cells[normalizeHeader(ext)] ?? "").trim();
  if (id !== "") return `ext:${id}`;
  const parts: [string, string][] = [];
  for (const t of TASK_IMPORT_TARGETS) {
    const c = mapping.columns[t];
    if (c !== undefined) parts.push([t, (cells[normalizeHeader(c)] ?? "").trim()]);
  }
  for (const [fid, c] of Object.entries(mapping.fields ?? {}).sort(([a], [b]) =>
    a.localeCompare(b),
  ))
    parts.push([fid, (cells[normalizeHeader(c)] ?? "").trim()]);
  return `row:${JSON.stringify(parts)}`;
}

/**
 * A lenient RFC 4180 read of the first `limit` data rows for the wizard's mapping and preview
 * (quoted fields with commas, `""` and line breaks; CRLF or LF; a BOM dropped; blank lines
 * skipped). The server reads the file again with its strict, bounded reader; nothing here
 * decides what is imported. Cells are keyed by normalised header.
 */
export function previewCsv(
  text: string,
  limit = 5,
): { header: string[]; rows: Record<string, string>[]; total: number } {
  const recs: string[][] = [];
  let rec: string[] = [];
  let field = "";
  let quoted = false;
  const body = text.startsWith("﻿") ? text.slice(1) : text;
  const endRecord = () => {
    rec.push(field);
    field = "";
    if (!(rec.length === 1 && rec[0] === "")) recs.push(rec);
    rec = [];
  };
  for (let i = 0; i < body.length; i++) {
    const ch = body[i] as string;
    if (quoted) {
      if (ch === '"' && body[i + 1] === '"') {
        field += '"';
        i++;
      } else if (ch === '"') quoted = false;
      else field += ch;
    } else if (ch === '"' && field === "") quoted = true;
    else if (ch === ",") {
      rec.push(field);
      field = "";
    } else if (ch === "\r" || ch === "\n") {
      if (ch === "\r" && body[i + 1] === "\n") i++;
      endRecord();
    } else field += ch;
  }
  if (field !== "" || rec.length > 0) endRecord();
  const [head = [], ...data] = recs;
  const header = head.map((h) => h.trim());
  const keys = header.map(normalizeHeader);
  return {
    header,
    rows: data
      .slice(0, limit)
      .map((cells) => Object.fromEntries(keys.map((k, j) => [k, (cells[j] ?? "").trim()]))),
    total: data.length,
  };
}
